import { useState } from "react"
import { FaFolder, FaFolderOpen, FaPlus, FaEllipsisV } from "react-icons/fa"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/modules/shared/ui/components/dropdown-menu"
import { Button } from "@/modules/shared/ui/components/button"
import { Skeleton } from "@/modules/shared/ui/components/skeleton"
import { useFolderTree, useCreateFolder } from "../../hooks/useFolders"
import { FolderTreeDto } from "../../entities/FolderTreeDto"
import { CreateRenameFolderDialog } from "./CreateRenameFolderDialog"
import { DeleteFolderDialog } from "./DeleteFolderDialog"

interface FolderTreeBrowserProps {
  selectedFolderId: string | undefined
  onSelectFolder: (folderId: string | undefined) => void
  onManageFolderPermissions: (folderId: string, folderName: string) => void
}

interface FolderNodeProps {
  node: FolderTreeDto
  depth: number
  selectedFolderId: string | undefined
  onSelectFolder: (folderId: string | undefined) => void
  onManageFolderPermissions: (folderId: string, folderName: string) => void
}

function FolderNode({
  node,
  depth,
  selectedFolderId,
  onSelectFolder,
  onManageFolderPermissions,
}: FolderNodeProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [createSubOpen, setCreateSubOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const isSelected = selectedFolderId === node.id
  const hasChildren = node.children.length > 0

  return (
    <div>
      <div
        className={`group flex items-center gap-1 rounded-md px-2 py-1 text-sm cursor-pointer transition-colors ${
          isSelected
            ? "bg-primary/10 text-primary font-medium"
            : "hover:bg-muted/50 text-foreground"
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        <button
          type="button"
          className="flex items-center gap-2 flex-1 min-w-0"
          onClick={() => {
            onSelectFolder(isSelected ? undefined : node.id)
            if (hasChildren) setIsExpanded((e) => !e)
          }}
        >
          {isSelected || isExpanded ? (
            <FaFolderOpen className="h-3.5 w-3.5 text-amber-500 shrink-0" />
          ) : (
            <FaFolder className="h-3.5 w-3.5 text-amber-400 shrink-0" />
          )}
          <span className="truncate">{node.name}</span>
        </button>

        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              <FaEllipsisV className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => setCreateSubOpen(true)}>
              <FaPlus className="mr-2 h-3 w-3" />
              Create subfolder
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setRenameOpen(true)}>
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onManageFolderPermissions(node.id, node.name)}
            >
              Manage Permissions
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <FolderNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedFolderId={selectedFolderId}
              onSelectFolder={onSelectFolder}
              onManageFolderPermissions={onManageFolderPermissions}
            />
          ))}
        </div>
      )}

      <CreateRenameFolderDialog
        mode="create"
        parentFolderId={node.id}
        open={createSubOpen}
        onOpenChange={setCreateSubOpen}
      />

      <CreateRenameFolderDialog
        mode="rename"
        folderId={node.id}
        existingName={node.name}
        open={renameOpen}
        onOpenChange={setRenameOpen}
      />

      <DeleteFolderDialog
        folderId={node.id}
        folderName={node.name}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={() => {
          if (selectedFolderId === node.id) onSelectFolder(undefined)
        }}
      />
    </div>
  )
}

export function FolderTreeBrowser({
  selectedFolderId,
  onSelectFolder,
  onManageFolderPermissions,
}: FolderTreeBrowserProps) {
  const { data: tree = [], isLoading } = useFolderTree()
  const createFolder = useCreateFolder()
  const [createRootOpen, setCreateRootOpen] = useState(false)

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between px-2 py-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Folders
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          title="New root folder"
          onClick={() => setCreateRootOpen(true)}
        >
          <FaPlus className="h-3 w-3" />
        </Button>
      </div>

      <button
        type="button"
        className={`flex items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors ${
          !selectedFolderId
            ? "bg-primary/10 text-primary font-medium"
            : "hover:bg-muted/50 text-muted-foreground"
        }`}
        onClick={() => onSelectFolder(undefined)}
      >
        <FaFolderOpen className="h-3.5 w-3.5 text-muted-foreground" />
        All Files
      </button>

      {isLoading ? (
        <div className="space-y-1 px-2">
          {[1, 2, 3].map((n) => (
            <Skeleton key={n} className="h-7 w-full" />
          ))}
        </div>
      ) : tree.length === 0 ? (
        <p className="px-2 py-2 text-xs text-muted-foreground">
          No folders yet
        </p>
      ) : (
        tree.map((node) => (
          <FolderNode
            key={node.id}
            node={node}
            depth={0}
            selectedFolderId={selectedFolderId}
            onSelectFolder={onSelectFolder}
            onManageFolderPermissions={onManageFolderPermissions}
          />
        ))
      )}

      <CreateRenameFolderDialog
        mode="create"
        open={createRootOpen}
        onOpenChange={setCreateRootOpen}
        onCreate={(name) => createFolder.mutate({ name, parentFolderId: null })}
      />
    </div>
  )
}
